import React, { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Calendar, AlertTriangle, CheckCircle, Eye, Play } from "lucide-react";
import { useToast } from "../../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BulkDateFix = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(false);

  const headers = { Authorization: 'Bearer cookie-auth' };

  const runBulkFix = async (apply = false) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/admin/operations/bulk-fix-dates`,
        { apply },
        { headers }
      );
      setPreview(res.data);
      if (apply) {
        setApplied(true);
        toast({ title: "Corrections appliquées", description: `${res.data.operations_corrigées} opération(s) corrigée(s)` });
      }
    } catch (err) {
      toast({ title: "Erreur", description: err.response?.data?.detail || "Erreur serveur", variant: "destructive" });
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="w-5 h-5" />
            Correction en masse des dates importées
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Corrige automatiquement les opérations où <code className="bg-gray-100 px-1 rounded">createdAt</code> (date d'import) 
            ne correspond pas à <code className="bg-gray-100 px-1 rounded">deliveryDate</code> (date réelle).
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-amber-800">Corrections appliquées :</p>
                <ul className="mt-1 text-amber-700 space-y-0.5">
                  <li><code>createdAt</code> → <code>deliveryDate</code> (date de l'opération)</li>
                  <li><code>invoiceGeneratedAt</code> → <code>invoiceDepositDate</code> (date de facturation réelle)</li>
                  <li><code>invoiceSentAt</code> → <code>invoiceDepositDate</code> (date de facturation réelle)</li>
                </ul>
                <p className="mt-1 text-amber-600 text-xs">Seules les opérations avec un écart &gt; 7 jours sont corrigées.</p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={() => runBulkFix(false)}
              disabled={loading}
              variant="outline"
              className="flex items-center gap-2"
              data-testid="bulk-fix-preview"
            >
              <Eye className="w-4 h-4" />
              {loading ? "Analyse..." : "Aperçu (sans modifier)"}
            </Button>
            <Button
              onClick={() => runBulkFix(true)}
              disabled={loading || applied}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="bulk-fix-apply"
            >
              <Play className="w-4 h-4" />
              {loading ? "Application..." : applied ? "Déjà appliqué" : "Appliquer les corrections"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {preview && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              {preview.mode === "APPLIQUÉ" ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <Eye className="w-5 h-5 text-blue-600" />
              )}
              Résultat — {preview.mode}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-700">{preview.operations_corrigées}</p>
                <p className="text-xs text-green-600">À corriger</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-gray-700">{preview.operations_ignorées}</p>
                <p className="text-xs text-gray-500">Ignorées (OK)</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-700">{preview.total}</p>
                <p className="text-xs text-blue-500">Total</p>
              </div>
            </div>

            {preview.détails && preview.détails.length > 0 && (
              <div className="max-h-[400px] overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Opération</th>
                      <th className="text-left px-3 py-2 font-medium">Champ</th>
                      <th className="text-left px-3 py-2 font-medium">Ancien</th>
                      <th className="text-left px-3 py-2 font-medium">Nouveau</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {preview.détails.map((op, i) =>
                      op.changes.map((c, j) => (
                        <tr key={`${i}-${j}`} className="hover:bg-gray-50">
                          {j === 0 && (
                            <td className="px-3 py-1.5 font-mono text-xs" rowSpan={op.changes.length}>
                              {op.operationNumber}
                            </td>
                          )}
                          <td className="px-3 py-1.5 text-gray-600">{c.champ}</td>
                          <td className="px-3 py-1.5 text-red-600 line-through">{c.ancien}</td>
                          <td className="px-3 py-1.5 text-green-600 font-medium">{c.nouveau}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BulkDateFix;
